import { useTradingSettings } from '@src/providers/AppConfigProvider';
import {
  AccordionElement,
  DateRangePicker,
  DEFAULT_LOOKBACK_OPTIONS,
  FilterBuilder,
  FormControlSizes,
  Icon,
  IconButton,
  IconName,
  IndicatorDotVariants,
  IndicatorDotWrapper,
  Input,
  MixpanelEvent,
  MixpanelEventProperty,
  MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY,
  MOBILE_HEADER_TOOLBAR_PORTAL,
  MOBILE_MODAL_CONTENT_PORTAL,
  Portal,
  SearchSelect,
  Stack,
  Text,
  Toggle,
  useCallbackRef,
  useMixpanel,
  useMobileModal,
  type BlotterTableFiltersProps,
  type TimestampField,
  type UseAccordionFilterBuilderOutput,
} from '@talos/kyoko';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MobileBlotterSearchBar, MobileBlotterSheetSection, MobileFilterBuilderWrapper } from './styles';

type ActiveMobileBlotterSheet = 'filters' | 'settings';

type MobileBlotterToolbarProps<TTimestampField extends string = string> = {
  filterBuilder?: UseAccordionFilterBuilderOutput['filterBuilder'];
  dateRange?: BlotterTableFiltersProps<TTimestampField>['dateRange'];
  dateRangeShortcuts?: BlotterTableFiltersProps<TTimestampField>['dateRangeShortcuts'];
  onDateRangeChanged?: BlotterTableFiltersProps<TTimestampField>['onDateRangeChanged'];
  canSubmitClearedDateRange?: BlotterTableFiltersProps<TTimestampField>['canSubmitClearedDateRange'];
  dateRangePickerClearable?: BlotterTableFiltersProps<TTimestampField>['dateRangePickerClearable'];
  selectedTimestampField?: TTimestampField;
  timestampFields?: TimestampField<TTimestampField>[];
  onTimestampFieldChanged?: BlotterTableFiltersProps<TTimestampField>['onTimestampFieldChanged'];
  accordionBodyPrefix?: ReactNode;
  settingsContent?: ReactNode;
  hasActiveFilters?: boolean;
  showDateRangePicker?: boolean;
  showFilterBuilder?: boolean;
  showQuickFilter?: BlotterTableFiltersProps<TTimestampField>['showQuickFilter'];
  quickFilterPlaceholder?: BlotterTableFiltersProps<TTimestampField>['quickFilterPlaceholder'];
  quickFilterText?: BlotterTableFiltersProps<TTimestampField>['quickFilterText'];
  onQuickFilterTextChanged?: BlotterTableFiltersProps<TTimestampField>['onQuickFilterTextChanged'];
};

export function MobileBlotterToolbar<TTimestampField extends string = string>({
  filterBuilder,
  dateRange,
  dateRangeShortcuts = DEFAULT_LOOKBACK_OPTIONS,
  onDateRangeChanged,
  canSubmitClearedDateRange,
  dateRangePickerClearable = true,
  selectedTimestampField,
  timestampFields,
  onTimestampFieldChanged,
  accordionBodyPrefix,
  settingsContent,
  hasActiveFilters,
  showDateRangePicker = true,
  showFilterBuilder = true,
  showQuickFilter = true,
  quickFilterPlaceholder,
  quickFilterText,
  onQuickFilterTextChanged,
}: MobileBlotterToolbarProps<TTimestampField>) {
  const mixpanel = useMixpanel();
  const { isModalOpen, openModal } = useMobileModal();
  const { showMobileQuickSearch, setShowMobileQuickSearch } = useTradingSettings();
  const [activeSheet, setActiveSheet] = useState<ActiveMobileBlotterSheet>();
  const searchBarRef = useRef<HTMLDivElement>(null);

  const resolvedHasActiveFilters = hasActiveFilters ?? (filterBuilder?.activeFilterCount ?? 0) > 0;
  const hasSettingsSheet = showQuickFilter || settingsContent != null;

  const filterSheetContent = useMemo(
    () => (
      <MobileBlotterFilterSheet
        filterBuilder={filterBuilder}
        dateRange={dateRange}
        dateRangeShortcuts={dateRangeShortcuts}
        onDateRangeChanged={onDateRangeChanged}
        canSubmitClearedDateRange={canSubmitClearedDateRange}
        dateRangePickerClearable={dateRangePickerClearable}
        selectedTimestampField={selectedTimestampField}
        timestampFields={timestampFields}
        onTimestampFieldChanged={onTimestampFieldChanged}
        accordionBodyPrefix={accordionBodyPrefix}
        showDateRangePicker={showDateRangePicker}
        showFilterBuilder={showFilterBuilder}
      />
    ),
    [
      canSubmitClearedDateRange,
      dateRange,
      dateRangePickerClearable,
      dateRangeShortcuts,
      filterBuilder,
      accordionBodyPrefix,
      onDateRangeChanged,
      onTimestampFieldChanged,
      selectedTimestampField,
      showDateRangePicker,
      showFilterBuilder,
      timestampFields,
    ]
  );

  const handleSearchOpenChanged = useCallbackRef((nextIsSearchOpen: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleQuickSearch, {
      [MixpanelEventProperty.Enabled]: nextIsSearchOpen,
    });

    if (!nextIsSearchOpen) {
      onQuickFilterTextChanged?.('');
    }
    setShowMobileQuickSearch(nextIsSearchOpen);
  });

  const settingsSheetContent = useMemo(
    () => (
      <MobileBlotterSettingsSheet>
        {showQuickFilter ? (
          <Toggle
            label="Show quick search"
            size={FormControlSizes.Default}
            checked={showMobileQuickSearch}
            onChange={handleSearchOpenChanged}
            fullWidth
          />
        ) : null}
        {settingsContent}
      </MobileBlotterSettingsSheet>
    ),
    [handleSearchOpenChanged, settingsContent, showMobileQuickSearch, showQuickFilter]
  );

  const clearActiveSheet = useCallback(() => setActiveSheet(undefined), []);

  const openFilterSheet = useCallback(() => {
    setActiveSheet('filters');
    openModal({
      title: 'Filters',
      useSheetContent: true,
      onCloseModal: clearActiveSheet,
    });
  }, [clearActiveSheet, openModal]);

  const openSettingsSheet = useCallback(() => {
    setActiveSheet('settings');
    openModal({
      title: 'Settings',
      useSheetContent: true,
      onCloseModal: clearActiveSheet,
    });
  }, [clearActiveSheet, openModal]);

  const handleOnFilterTextChanged: React.ChangeEventHandler<HTMLInputElement> = useCallbackRef(e => {
    mixpanel.track(MixpanelEvent.FilterQuickSearch);
    onQuickFilterTextChanged?.(e.target.value);
  });

  useEffect(() => {
    if (!isModalOpen) {
      clearActiveSheet();
    }
  }, [clearActiveSheet, isModalOpen]);

  useLayoutEffect(() => {
    const table = document.querySelector<HTMLElement>('[data-testid="mobile-data-table"]');
    const searchBar = searchBarRef.current;
    if (!showQuickFilter || table == null || searchBar == null) {
      return;
    }

    const baseTopPaddingPx =
      parseFloat(getComputedStyle(table).getPropertyValue(MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY)) || 0;

    const syncOffset = () => {
      table.style.setProperty(MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY, `${baseTopPaddingPx + searchBar.offsetHeight}px`);
    };
    syncOffset();
    const observer = new ResizeObserver(syncOffset);
    observer.observe(searchBar);
    return () => {
      observer.disconnect();
      table.style.removeProperty(MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY);
    };
  }, [showQuickFilter]);

  return (
    <>
      <Portal portalId={MOBILE_HEADER_TOOLBAR_PORTAL}>
        <IndicatorDotWrapper
          show={resolvedHasActiveFilters}
          variant={IndicatorDotVariants.Notification}
          size={8}
          inset={6}
        >
          <IconButton
            data-testid="mobile-blotter-filter-toggle"
            icon={IconName.Filter}
            ghost
            round
            onClick={openFilterSheet}
          />
        </IndicatorDotWrapper>
      </Portal>
      {hasSettingsSheet ? (
        <Portal portalId={MOBILE_HEADER_TOOLBAR_PORTAL}>
          <IconButton
            data-testid="mobile-blotter-settings-toggle"
            icon={IconName.Preferences}
            ghost
            round
            onClick={openSettingsSheet}
            style={{ transform: 'rotate(90deg)' }}
          />
        </Portal>
      ) : null}
      {showQuickFilter ? (
        <MobileBlotterSearchBar ref={searchBarRef}>
          <AccordionElement
            isOpen={showMobileQuickSearch}
            w="100%"
            px="spacing16"
            py="spacing8"
            data-testid="mobile-blotter-search-bar"
          >
            <Input
              prefix={<Icon icon={IconName.Search} />}
              clearable={true}
              size={FormControlSizes.Default}
              width="100%"
              placeholder={quickFilterPlaceholder}
              value={quickFilterText}
              onChange={handleOnFilterTextChanged}
              data-testid="filter-search"
            />
          </AccordionElement>
        </MobileBlotterSearchBar>
      ) : null}
      {activeSheet != null ? (
        <Portal portalId={MOBILE_MODAL_CONTENT_PORTAL}>
          {activeSheet === 'filters' ? filterSheetContent : settingsSheetContent}
        </Portal>
      ) : null}
    </>
  );
}

function MobileBlotterFilterSheet<TTimestampField extends string = string>({
  filterBuilder,
  dateRange,
  dateRangeShortcuts,
  onDateRangeChanged,
  canSubmitClearedDateRange,
  dateRangePickerClearable,
  selectedTimestampField,
  timestampFields,
  onTimestampFieldChanged,
  accordionBodyPrefix,
  showDateRangePicker,
  showFilterBuilder,
}: Omit<
  MobileBlotterToolbarProps<TTimestampField>,
  | 'settingsContent'
  | 'hasActiveFilters'
  | 'showQuickFilter'
  | 'quickFilterPlaceholder'
  | 'quickFilterText'
  | 'onQuickFilterTextChanged'
>) {
  const timestampFieldOptions = useMemo(() => timestampFields?.map(tsf => tsf.Name), [timestampFields]);
  const defaultTimestampField = useMemo(() => timestampFields?.find(tsf => tsf.default)?.Name, [timestampFields]);
  const resolvedTimestampField = selectedTimestampField ?? defaultTimestampField;
  const shortcutOptions = useMemo(() => dateRangeShortcuts?.map(value => ({ value })), [dateRangeShortcuts]);

  const getTimestampFieldLabel = useCallback(
    (timestampField: TTimestampField) =>
      timestampFields?.find(field => field.Name === timestampField)?.DisplayName ?? timestampField,
    [timestampFields]
  );

  return (
    <Stack gap="spacing24" data-testid="mobile-blotter-filter-sheet">
      {accordionBodyPrefix != null ? (
        <MobileBlotterSheetSection>
          <Text>Group by</Text>
          {accordionBodyPrefix}
        </MobileBlotterSheetSection>
      ) : null}
      {showDateRangePicker && dateRange != null ? (
        <MobileBlotterSheetSection>
          <Text>Date range</Text>
          <DateRangePicker
            data-testid="mobile-blotter-date-range-picker"
            size={FormControlSizes.Default}
            onChange={newDateRange => onDateRangeChanged?.(newDateRange)}
            value={dateRange}
            shortcuts={shortcutOptions}
            clearable={dateRangePickerClearable}
            canSubmitCleared={canSubmitClearedDateRange}
            width="100%"
          />
          {timestampFieldOptions && timestampFieldOptions.length > 0 ? (
            <SearchSelect
              prefix={
                <Text color="colorTextSubtle" mr="spacing8">
                  Timestamp type
                </Text>
              }
              dataTestID="mobile-blotter-timestamp-field-select"
              selection={resolvedTimestampField}
              options={timestampFieldOptions}
              getLabel={getTimestampFieldLabel}
              onChange={newValue => {
                if (newValue != null) {
                  onTimestampFieldChanged?.(newValue);
                }
              }}
              size={FormControlSizes.Default}
              showDropdownSearch={false}
              portalize
            />
          ) : null}
        </MobileBlotterSheetSection>
      ) : null}
      {showFilterBuilder && filterBuilder != null ? (
        <MobileBlotterSheetSection>
          <Text>Filters</Text>
          <MobileFilterBuilderWrapper>
            <FilterBuilder {...filterBuilder} showClearFilters={true} maxVisibleSelectionsPerProperty={1} />
          </MobileFilterBuilderWrapper>
        </MobileBlotterSheetSection>
      ) : null}
    </Stack>
  );
}

function MobileBlotterSettingsSheet({ children }: { children: ReactNode }) {
  return (
    <Stack gap="spacing12" data-testid="mobile-blotter-settings-sheet">
      {children ?? <Text color="gray.080">No settings available.</Text>}
    </Stack>
  );
}
